import { Editor } from '@tiptap/react';
import { useCallback } from 'react';

export interface TextmenuCommandsProps {
  onBold: () => void;
  onItalic: () => void;
  onUnderline: () => void;
  onStrike: () => void;
  onAlignLeft: () => void;
  onAlignCenter: () => void;
  onAlignRight: () => void;
  onAlignJustify: () => void;
  onSetFontSize: (fontSize: string) => void;
  onChangeColor: (color: string) => void;
  onClearColor: () => void;
  onChangeHighlight: (color: string) => void;
  onClearHighlight: () => void;
  onSetLink: ({ url }: SetLinkProps) => void;
  onUnsetLink: () => void;
}

export interface SetLinkProps {
  url: string;
}

export const useTextMenuCommands = (editor: Editor): TextmenuCommandsProps => {
  const onBold = useCallback(() => editor.chain().focus().toggleBold().run(), [editor]);
  const onItalic = useCallback(() => editor.chain().focus().toggleItalic().run(), [editor]);
  const onUnderline = useCallback(() => editor.chain().focus().toggleUnderline().run(), [editor]);
  const onStrike = useCallback(() => editor.chain().focus().toggleStrike().run(), [editor]);
  const onAlignLeft = useCallback(() => editor.chain().focus().setTextAlign('left').run(), [editor]);
  const onAlignCenter = useCallback(() => editor.chain().focus().setTextAlign('center').run(), [editor]);
  const onAlignRight = useCallback(() => editor.chain().focus().setTextAlign('right').run(), [editor]);
  const onAlignJustify = useCallback(() => editor.chain().focus().setTextAlign('justify').run(), [editor]);
  const onChangeColor = useCallback((color: string) => editor.chain().setColor(color).run(), [editor]);
  const onClearColor = useCallback(() => editor.chain().focus().unsetColor().run(), [editor]);
  const onChangeHighlight = useCallback((color: string) => editor.chain().setHighlight({ color }).run(), [editor]);
  const onClearHighlight = useCallback(() => editor.chain().focus().unsetHighlight().run(), [editor]);

  const onSetFontSize = useCallback(
    (fontSize: string) => {
      if (!fontSize || fontSize.length === 0) {
        return editor.chain().focus().unsetFontSize().run();
      }
      return editor.chain().focus().setFontSize(fontSize).run();
    },
    [editor],
  );

  const onSetLink = useCallback(
    ({ url }: SetLinkProps) => {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url, target: '_blank' })
        .run();
    },
    [editor],
  );

  const onUnsetLink = useCallback(() => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  }, [editor]);

  return {
    onBold,
    onItalic,
    onUnderline,
    onStrike,
    onAlignLeft,
    onAlignCenter,
    onAlignRight,
    onAlignJustify,
    onSetFontSize,
    onChangeColor,
    onClearColor,
    onChangeHighlight,
    onClearHighlight,
    onSetLink,
    onUnsetLink,
  };
};
